import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  TablePagination,
  TextField,
} from "@mui/material";
import { useParams } from "react-router-dom";

import {
  OrderBetween,
  Replacement,
} from "@safetyculture/s12-apis-connect-web/s12/accounts/admin/v1/white_label_pb";

import CustomerLabelCreate from "./custom-labels-create";
import CustomerLabelDelete from "./custom-labels-delete";
import CustomerLabelEdit from "./custom-labels-edit";
import CustomerLabelMove from "./custom-labels-move";
import {
  ColumnHeader,
  CustomNoRowsOverlay,
  LoadingOverlay,
  ReplacementRow,
  RowItem,
} from "./custom-labels-table-components";
import { UpdateWhiteLabelReplacement } from "api/endpoints/custom-labels";
import { callOptionsHeaders } from "api/helpers";
import { UrlParamTypes } from "types/org-display";

const OrganisationCustomLabelsTable = ({
  customLabels,
  isLoading,
  onUpdated = () => {},
}: {
  customLabels: Replacement[];
  isLoading: boolean;
  onUpdated: () => void;
}) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState("");
  const [editMode, setEditMode] = useState<{
    replacement: Replacement;
    order: OrderBetween;
  } | null>(null);
  const [moveMode, setMoveMode] = useState<ReplacementRow | null>(null); // Fix: Use Replacement instead of ReplacementRow[]
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [deleteMode, setDeleteMode] = useState<Replacement | null>(null);
  const [rows, setRows] = useState<ReplacementRow[]>(
    customLabels.map((row, index) => ({
      ...row,
      originalIndex: index, // Add the original index to each row
    }))
  );
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setRows(
      customLabels.map((row, index) => ({
        ...row,
        originalIndex: index,
      }))
    );
  }, [customLabels]);

  const updateReplacement = async (
    source: Replacement,
    prevKey?: string,
    nextKey?: string
  ) => {
    try {
      setLoading(true);
      const orderBetween = new OrderBetween({
        prevKey: prevKey || "",
        nextKey: nextKey || "",
      });

      await UpdateWhiteLabelReplacement(
        {
          orgId: roleId,
          id: source.id,
          searchValue: source.searchValue,
          replaceValue: source.replaceValue,
          order: orderBetween,
        },
        callOptionsHeaders(regionCode, roleId)
      );

      onUpdated();
    } catch (error) {
      console.error("Failed to update replacement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => setSearchText(value);

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = searchText
    ? rows.filter(
        (row) =>
          row.id.toLowerCase().includes(searchText.toLowerCase()) ||
          row.searchValue.toLowerCase().includes(searchText.toLowerCase()) ||
          row.replaceValue.toLowerCase().includes(searchText.toLowerCase())
      )
    : rows;

  const paginatedRows = filteredRows.slice(
    page * pageSize,
    page * pageSize + pageSize
  );

  const clearForm = () => {
    setEditMode(null);
    setDeleteMode(null);
    setMoveMode(null);
    setCreateMode(false);
  };

  const handleDrag = (sourceIndex: number, destinationIndex: number) => {
    const actualSourceIndex = filteredRows[sourceIndex].originalIndex;
    const actualDestinationIndex = filteredRows[destinationIndex].originalIndex;

    let prevKey: string;
    let nextKey: string;

    if (destinationIndex > sourceIndex) {
      prevKey = rows[actualDestinationIndex].orderKey;
      nextKey = rows[actualDestinationIndex + 1]?.orderKey || "";
    } else if (destinationIndex < sourceIndex) {
      prevKey = rows[actualDestinationIndex - 1]?.orderKey || "";
      nextKey = rows[actualDestinationIndex].orderKey;
    } else {
      return;
    }

    const draggedReplacement: Replacement = new Replacement(
      rows[actualSourceIndex]
    );

    // Update the backend with the new order
    updateReplacement(draggedReplacement, prevKey, nextKey);

    // Reorder the rows array
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(actualSourceIndex, 1);
    reorderedRows.splice(actualDestinationIndex, 0, removed);
    setRows(reorderedRows);
  };

  React.useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceIndex = source.data.index as number;
        const destinationIndex = destination.data.index as number;

        if (sourceIndex !== destinationIndex) {
          handleDrag(sourceIndex, destinationIndex);
        }
      },
    });
  }, [filteredRows, rows]);

  return isLoading ? (
    <CircularProgress />
  ) : (
    <>
      <Box sx={{ maxHeight: "15vh", width: "100%" }} marginBottom={0}>
        <Grid
          display="flex"
          justifyContent="space-between"
          marginBottom={1}
          gap={2}
        >
          <TextField
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            label="Search..."
            disabled={isLoading}
            data-testid="org-custom-labels-search-field"
            sx={{ width: "85%" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              clearForm();
              setCreateMode(true);
            }}
          >
            Create Label
          </Button>
        </Grid>
      </Box>
      {customLabels.length === 0 ? (
        <CustomNoRowsOverlay />
      ) : (
        <>
          <Box sx={{ position: "relative" }}>
            {loading && <LoadingOverlay />}
            <Box
              sx={{
                pointerEvents: loading ? "none" : "auto",
              }}
            >
              <List>
                <ColumnHeader />
                {paginatedRows.map((row, index) => {
                  const actualIndex = page * pageSize + index;
                  return (
                    <RowItem
                      key={row.id}
                      row={row}
                      index={actualIndex}
                      paginatedRows={paginatedRows}
                      onEdit={(row) => {
                        setEditMode({
                          replacement: row,
                          order: new OrderBetween({
                            prevKey: rows[actualIndex - 1]?.orderKey || "",
                            nextKey: rows[actualIndex + 1]?.orderKey || "",
                          }),
                        });
                      }}
                      onDelete={(row) => setDeleteMode(row)}
                      onDrag={handleDrag}
                      onMove={(row) =>
                        setMoveMode({
                          ...row,
                          originalIndex: rows.findIndex((r) => r.id === row.id),
                        })
                      }
                    />
                  );
                })}
              </List>
            </Box>
          </Box>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
      {editMode && (
        <CustomerLabelEdit
          replacement={editMode.replacement}
          order={editMode.order}
          onCancel={clearForm}
          onComplete={() => {
            clearForm();
            onUpdated();
          }}
        />
      )}
      {createMode && (
        <CustomerLabelCreate
          onCancel={clearForm}
          onComplete={() => {
            clearForm();
            onUpdated();
          }}
          replacements={rows}
        />
      )}
      {moveMode && (
        <CustomerLabelMove
          replacement={moveMode}
          rows={rows}
          setRows={setRows}
          onComplete={() => {
            clearForm();
            onUpdated();
          }}
          onCancel={clearForm}
        />
      )}
      {deleteMode && (
        <CustomerLabelDelete
          replacement={deleteMode}
          onCancel={clearForm}
          onComplete={() => {
            clearForm();
            onUpdated();
          }}
        />
      )}
    </>
  );
};

export default OrganisationCustomLabelsTable;
